import Select from 'react-select'
import { useForm, Controller } from 'react-hook-form'

import React, { useRef } from 'react'
import { Multiselect } from 'multiselect-react-dropdown'

import { EventInfoBadges } from './EventInfoBadges'
import { Button, Card, Col, Collapse, Form, InputGroup, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { setFilter } from '../../store/events/events'
import { isSmallMobileQuery, isTabletOrMobileQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import moment from 'moment'
import { RootState } from '../../store'
import { useAppDispatch, useAppSelector } from '../../hooks'
import CustomDatePicker, { CustomDatePickerRef } from '../shared/DatePicker'

export type FilterState = {
  applyFiltering: boolean
  keyword: string
  age: number
  gender: string
  start: string //TODO: date?
  end: string //TODO: or be a Date?
  notFullOnly: boolean
  adultsOnly: boolean
  uncanceledOnly: boolean
  accessibleOnly: boolean
  supportedDisabilities: { value: string; label: string }[]
  premarkedOnly: boolean
  tags: string[]
  isExpanded: boolean
}

const accessibilityOptions = [
  { value: 'wheelchair', label: ' Gehbeeinträchtigung' },
  { value: 'blind', label: 'Sehbeeinträchtigung' },
  { value: 'deaf', label: 'Hörbeeinträchtigung' },
]

type _FilterState = FilterState & {
  start: Date
  end: Date
}

export const FilterForm = () => {
  const { data: program } = useAppSelector((state: RootState) => state.program)
  const filterState = useAppSelector((state: RootState) => state.events.filter)
  const tagOptions: { name: string; id: number }[] = []
  //TODO: fix logo?
  program.available_tags.forEach((tag, i) => {
    tagOptions.push({ name: tag, id: i + 1 })
  })
  const dispatch = useAppDispatch()

  const { register, handleSubmit, control, reset } = useForm<FilterState>({
    defaultValues: filterState,
  })
  const multiselectRef = useRef()
  const startDateRef = useRef<CustomDatePickerRef>(null)
  const endDateRef = useRef<CustomDatePickerRef>(null)

  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery)
  const isSmallMobile = useMediaQuery(isSmallMobileQuery)

  function onKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    let navButtonFocus = false
    const activeElement = document.activeElement
    if (activeElement) {
      navButtonFocus = activeElement.className.includes('filter-nav-button')
    }
    if (e.key === 'Enter' && !navButtonFocus) {
      e.preventDefault()
      const inputElement = activeElement as HTMLInputElement
      if (inputElement.className.includes('form-check-input')) {
        inputElement.checked = !inputElement.checked
      }
    }
  }

  const onSubmit = handleSubmit((data: FilterState) => {
    const startDate = startDateRef?.current?.getStateVariable()
    const endDate = endDateRef?.current?.getStateVariable()
    dispatch(
      setFilter({
        ...data,
        start: startDate ? moment(startDate).format('DD.MM.YYYY') : undefined,
        end: endDate ? moment(endDate).format('DD.MM.YYYY') : undefined,
        // @ts-ignore
        tags: multiselectRef.current
          .getSelectedItems()
          .map((item: { name: string; id: number }) => item.name),
      }),
    )
  })

  return (
    <>
      <Card
        className='mt-3 rounded-0'
        role='region'
        arial-label='Filter Optionen für die Veranstaltungen'
      >
        <Card.Body>
          <Row>
            <Col md={12}>
              <p>
                Durchstöbere den Veranstaltungskatalog und wähle mehrere
                Veranstaltungen aus.
              </p>
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer>
          <Row className='m-0'>
            <Col md={12} className='d-flex pb-3 border-bottom flex-wrap'>
              <EventInfoBadges />
            </Col>

            <Col md={12} className='mt-3'>
              <h4>Veranstaltungsfilter</h4>
              <Form
                id='filterForm'
                onSubmit={onSubmit}
                onKeyDown={e => onKeyDown(e)}
              >
                <Form.Row>
                  <Col md={4} sm={12}>
                    <Form.Group controlId='keyword'>
                      <Form.Label>Stichwort:</Form.Label>
                      <Form.Control
                        type='text'
                        defaultValue=''
                        {...register('keyword')}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={12}>
                    <Form.Group controlId='age'>
                      <Form.Label>Alter:</Form.Label>
                      <InputGroup>
                        <Form.Control
                          {...register('age')}
                          type='number'
                          defaultValue={''}
                          autoComplete='bday-year'
                        />
                        <InputGroup.Append>
                          <InputGroup.Text id='ageAppend'>
                            Jahre
                          </InputGroup.Text>
                        </InputGroup.Append>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={12}>
                    <Form.Group controlId='gender'>
                      <Form.Label>Geschlecht</Form.Label>
                      <Form.Control
                        as='select'
                        defaultValue='e'
                        className='custom-select'
                        {...register('gender')}
                        autoComplete='sex'
                      >
                        <option value='e'>Egal</option>
                        <option value='m'>Männlich</option>
                        <option value='w'>Weiblich</option>
                        <option value='d'>Divers</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Form.Row>
                <Collapse in={filterState.isExpanded}>
                  <div>
                    <Form.Row>
                      <Col>
                        <Form.Label>Zeitraum:</Form.Label>
                        <Form.Group
                          controlId='keyword'
                          className='d-flex flex-direction-row'
                        >
                          <div className='input-group-prepend input-group-append'>
                            <InputGroup.Text
                              id='ageStart'
                              className='rounded-0'
                            >
                              <label htmlFor='start'>von</label>
                            </InputGroup.Text>
                          </div>
                          <InputGroup className='event-timespan'>
                            <Controller
                              name='start'
                              render={
                                ({ field }) =>
                                  <CustomDatePicker
                                    {...field}
                                    id='start'
                                    name='start'
                                    className='form-control w-100'
                                    aria-label='Zeitraum Start' //TODO: labels
                                    dateFormat='dd.MM.yyyy'
                                    isMobile={isTabletOrMobile}
                                    ref={startDateRef}
                                  />}
                              control={control}
                            />
                          </InputGroup>
                          <div className='input-group-prepend input-group-append'>
                            <InputGroup.Text id='ageEnd' className='rounded-0'>
                              <label htmlFor='end'>bis</label>
                            </InputGroup.Text>
                          </div>
                          <InputGroup className='event-timespan'>
                            <Controller
                              name='end'
                              render={({ field }) => <CustomDatePicker
                                {...field}
                                id='end'
                                name='end'
                                className='form-control w-100'
                                aria-label='Zeitraum Ende'
                                isMobile={isTabletOrMobile}
                                ref={endDateRef}
                              />
                              }
                              control={control}
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    </Form.Row>
                    <Form.Row>
                      <Col md={4} sm={12}>
                        <Form.Group controlId='tags'>
                          <Form.Label>Kategorien:</Form.Label>
                          <Multiselect
                            options={tagOptions}
                            displayValue='name'
                            // @ts-ignore TODO this is some legacy thing?!
                            ref={multiselectRef}
                            closeOnSelect={false}
                            customCloseIcon={
                              <>
                                &nbsp;
                                <FontAwesomeIcon
                                  icon='circle-xmark'
                                  aria-label='Kategorie entfernen'
                                />
                              </>
                            }
                            id='tags'
                            name='tags'
                            placeholder='Kategorien'
                          />
                        </Form.Group>
                      </Col>
                    </Form.Row>
                    <Form.Row>
                      <Col md={6} sm={12} className="mb-2">
                        <Form.Label>Unterstützte Beeinträchtigungen</Form.Label>
                        <Controller
                          name="supportedDisabilities"
                          control={control}
                          render={({field}) =>
                            <Select
                              {...field}
                              options={accessibilityOptions}
                              isMulti={true}
                              aria-label={'Unterstützte Beeinträchtigungen'}
                              placeholder={''}
                            />
                          }
                        />
                      </Col>
                    </Form.Row>

                    <hr />
                    <Form.Row>
                      <Col md={6} sm={12} className='mb-2'>
                        <Form.Check
                          type='checkbox'
                          id='premarkedEventsFlag'
                          label='Nur Vorgemerkte Veranstaltungen anzeigen'
                          defaultValue={0}
                          {...register('premarkedOnly')}
                        />
                      </Col>
                      <Col md={6} sm={12} className='mb-2'>
                        <Form.Check
                          type="checkbox"
                          id="adultEventsFlag"
                          label="Nur Veranstaltungen für Erwachsene anzeigen"
                          defaultValue={0}
                          {...register('adultsOnly')}
                        />
                      </Col>
                      <Col md={6} sm={12} className='mb-2'>
                        <Form.Check
                          type='checkbox'
                          id='fullEventsFlag'
                          label='Volle Veranstaltungen ausblenden'
                          defaultValue={0}
                          {...register('notFullOnly')}
                        />
                      </Col>
                      <Col md={6} sm={12} className='mb-2'>
                        <Form.Check
                          type='checkbox'
                          id='canceledEventsFlag'
                          label='Abgesagte Veranstaltungen ausblenden'
                          defaultValue={0}
                          {...register('uncanceledOnly')}
                        />
                      </Col>
                    </Form.Row>
                    <hr />
                  </div>
                </Collapse>
                <Form.Row>
                  <Col>
                    <Form.Group className='mb-0'>
                      <Button
                        type='submit'
                        variant='success'
                        className='mobile-btn mr-2 filter-nav-button'
                      >
                        <FontAwesomeIcon icon='search' role='presentation' />{' '}
                        Filter anwenden
                      </Button>
                      <Button
                        onClick={() => dispatch(setFilter({ ...filterState, isExpanded: !filterState.isExpanded }))}
                        aria-controls='filterForm'
                        aria-expanded={filterState.isExpanded}
                        variant='info'
                        className='mobile-btn mr-2 filter-nav-button'
                      >
                        {filterState.isExpanded ? (
                          <FontAwesomeIcon
                            icon='chevron-up'
                            role='presentation'
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon='chevron-down'
                            role='presentation'
                          />
                        )}
                        {filterState.isExpanded
                          ? ' Weniger Filter anzeigen'
                          : ' Mehr Filter anzeigen'}
                      </Button>
                      <Button
                        variant='light'
                        onClick={() => {
                          reset()
                          dispatch(setFilter({}))
                        }}
                        className='mobile-btn border-dark mb-0 filter-nav-button'
                      >
                        Filter zurücksetzen
                      </Button>
                    </Form.Group>
                  </Col>
                </Form.Row>
                <div
                  className={`d-flex justify-content-${isSmallMobile ? 'center' : 'end'
                  } mt-3`}
                >
                  <small className='muted-text'>
                    <FontAwesomeIcon icon='info-circle' role='presentation' />{' '}
                    Keines der Felder ist zwingend notwendig
                  </small>
                </div>
              </Form>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </>
  )
}
